// API client. Production calls https://eatrecipelabs.utilhub.in/api (VITE_API_BASE); development
// uses the Vite proxy at /api. Every library call sends the Bearer session; a 401 ends the session.
import { load, remove, save, KEYS } from './storage';
import type { Address, Dish, FoodCart, FoodOrder, GroceryCart, GroceryMatch, OrderStatus, Provider, AlbumsView, AlbumView, AlbumViewName, GroceryList, Hit, HomeView, Kitchen, Lang, KitchenNote, MyAlbum, Profile, ReelDetail, SavedReel, Surprise, Verdict } from './types';

const BASE = (import.meta.env.VITE_API_BASE as string | undefined)?.replace(/\/$/, '') || '/api';

/** A reel's cover image (public; plain <img> cannot send the session). */
export function thumbURL(reelId: number): string {
  return `${BASE}/thumbs/${reelId}`;
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

interface Session { token: string; expires_at: string }

export function getSession(): Session | null {
  const s = load<Session | null>(KEYS.session, null);
  if (!s || !s.token) return null;
  if (s.expires_at && Date.parse(s.expires_at) < Date.now()) {
    remove(KEYS.session);
    return null;
  }
  return s;
}

export function setSession(token: string, expiresAt: string) { save(KEYS.session, { token, expires_at: expiresAt }); }
export function clearSession() { remove(KEYS.session); }

type Unauthorized = () => void;
let onUnauthorized: Unauthorized = () => {};
export function setUnauthorizedHandler(fn: Unauthorized) { onUnauthorized = fn; }

async function request<T>(method: string, path: string, body?: unknown, opts: { auth?: boolean } = {}): Promise<T> {
  const headers: Record<string, string> = {};
  const auth = opts.auth ?? true;
  if (auth) {
    const s = getSession();
    if (!s) {
      onUnauthorized();
      throw new ApiError(401, 'Please sign in');
    }
    headers.Authorization = `Bearer ${s.token}`;
  }
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  let res: Response;
  try {
    res = await fetch(BASE + path, { method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
  } catch {
    throw new ApiError(0, "You're offline — showing what we last saw.");
  }
  if (res.status === 204) return undefined as T;
  let data: unknown = null;
  try {
    data = await res.json();
  } catch {
    /* non-JSON error page */
  }
  if (!res.ok) {
    const msg = (data as { error?: string } | null)?.error || `Something went wrong (${res.status})`;
    if (res.status === 401 && auth) {
      clearSession();
      onUnauthorized();
    }
    throw new ApiError(res.status, msg);
  }
  return data as T;
}

export const api = {
  preview: (token: string) => request<Profile>('POST', '/auth/preview', { token }, { auth: false }),
  exchange: (token: string) =>
    request<{ session: string; expires_at: string }>('POST', '/auth/exchange', { token }, { auth: false }),
  logout: () => request<void>('POST', '/auth/logout'),
  me: () => request<Profile>('GET', '/me'),
  deleteMe: () => request<void>('DELETE', '/me'),
  reels: () => request<{ reels: SavedReel[] }>('GET', '/reels').then((r) => r.reels),
  reel: (id: number) => request<ReelDetail>('GET', `/reels/${id}`),
  /** Where the reel's video streams from (Instagram's servers), for the in-app player. */
  reelVideo: (id: number) => request<{ url?: string; expires_at?: string; pending?: boolean }>('GET', `/reels/${id}/video`),
  addReel: (url: string) => request<SavedReel>('POST', '/reels', { url }),
  removeReel: (id: number) => request<void>('DELETE', `/reels/${id}`),
  retry: (id: number) => request<SavedReel>('POST', `/reels/${id}/retry`),
  search: (q: string) =>
    request<{ hits: Hit[] }>('GET', `/search?q=${encodeURIComponent(q)}`).then((r) => r.hits),
  setCooked: (id: number, cooked: boolean) => request<Kitchen>('POST', `/reels/${id}/cooked`, { cooked }),
  setVerdict: (id: number, verdict: Verdict) => request<void>('PUT', `/reels/${id}/verdict`, { verdict }),
  addNote: (id: number, text: string) => request<KitchenNote>('POST', `/reels/${id}/notes`, { text }),
  deleteNote: (noteId: number) => request<void>('DELETE', `/notes/${noteId}`),
  grocery: (ids: number[]) => request<GroceryList>('POST', '/grocery-list', { reel_ids: ids }),
  setLanguage: (language: Lang) => request<void>('PUT', '/me/language', { language }),
  home: () => request<HomeView>('GET', '/home'),
  albums: (view: AlbumViewName) => request<AlbumsView>('GET', `/albums?view=${view}`),
  album: (ref: string) => {
    const [kind, key] = ref.split(':');
    return request<AlbumView>('GET', `/albums/${kind}/${encodeURIComponent(key)}`);
  },
  pin: (ref: string, pinned: boolean) => {
    const [kind, key] = ref.split(':');
    return request<void>('PUT', `/albums/${kind}/${encodeURIComponent(key)}/pin`, { pinned });
  },
  createAlbum: (name: string) => request<MyAlbum>('POST', '/albums', { name }),
  renameAlbum: (id: number, name: string) => request<void>('PATCH', `/albums/mine/${id}`, { name }),
  deleteAlbum: (id: number) => request<void>('DELETE', `/albums/mine/${id}`),
  addToAlbum: (id: number, reelId: number) => request<void>('POST', `/albums/mine/${id}/recipes`, { reel_id: reelId }),
  removeFromAlbum: (id: number, reelId: number) => request<void>('DELETE', `/albums/mine/${id}/recipes/${reelId}`),
  surprise: (album: string, exclude: number[]) => request<Surprise>('POST', '/surprise', { album, exclude }),

  providers: () => request<{ providers: Provider[] }>('GET', '/commerce/providers').then((r) => r.providers),
  connect: (provider: string, returnTo: string) => request<{ url: string }>('POST', `/commerce/${provider}/connect`, { return_to: returnTo }),
  disconnect: (provider: string) => request<void>('DELETE', `/commerce/${provider}`),
  addresses: (provider: string) => request<{ addresses: Address[] }>('GET', `/commerce/${provider}/addresses`).then((r) => r.addresses),
  groceryMatch: (provider: string, addressId: string, reelIds: number[]) =>
    request<GroceryMatch>('POST', `/commerce/${provider}/grocery/match`, { address_id: addressId, reel_ids: reelIds }),
  groceryCart: (provider: string, addressId: string, lines: { item_id: string; quantity: number }[]) =>
    request<GroceryCart>('PUT', `/commerce/${provider}/grocery/cart`, { address_id: addressId, lines }),
  groceryOrder: (provider: string, body: { address_id: string; payment_method: string; confirmed: true }) =>
    request<FoodOrder>('POST', `/commerce/${provider}/grocery/order`, body),
  foodSearch: (provider: string, address: string, q: string, veg: boolean) =>
    request<{ dishes: Dish[] }>('GET', `/commerce/${provider}/food/search?q=${encodeURIComponent(q)}&address=${encodeURIComponent(address)}${veg ? '&veg=1' : ''}`).then((r) => r.dishes),
  foodCart: (provider: string, body: { address_id: string; restaurant_id: string; restaurant_name: string; items: { item_id: string; quantity: number }[] }) =>
    request<FoodCart>('PUT', `/commerce/${provider}/food/cart`, body),
  foodOrder: (provider: string, body: { address_id: string; payment_method: string; note: string; confirmed: true }) =>
    request<FoodOrder>('POST', `/commerce/${provider}/food/order`, body),
  orderPayment: (provider: string, orderId: string) => request<FoodOrder>('POST', `/commerce/${provider}/orders/${encodeURIComponent(orderId)}/payment`),
  orderTrack: (provider: string, orderId: string, addressId: string) =>
    request<OrderStatus>('GET', `/commerce/${provider}/orders/${encodeURIComponent(orderId)}?address=${encodeURIComponent(addressId)}`),
};
